(() => {
  'use strict';
  const drawer = document.querySelector('[data-lc-dialog]');
  if (!drawer || !('showModal' in drawer)) return;
  const root = (window.Shopify?.routes?.root || window.VH?.root || '/').replace(/\/?$/, '/');
  const select = selector => drawer.querySelector(selector);
  const content = select('[data-lc-content]');
  const status = select('[data-lc-status]');
  const errorBox = select('[data-lc-error]');
  const checkout = select('[data-lc-checkout]');
  const timer = select('[data-lc-timer]');
  const clock = select('[data-lc-clock]');
  const timerLabel = timer.querySelector('span');
  const labels = JSON.parse(document.querySelector('[data-lc-labels]').textContent);
  const seed = document.querySelector('[data-lc-cart-seed]');
  let cart = JSON.parse(seed.textContent);
  let busy = false;
  let uncertain = false;
  let lastSync = Date.now();
  let reading;
  let generation = 0;
  let opener;
  let oldOverflow = '';
  let timerInterval;
  let deadline = 0;
  const timerKey = 'varnholt-luciano-cart-deadline';
  const escape = value => String(value ?? '').replace(/[&<>"']/g, c => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
  let currency = '';
  let formatter;
  function money(amount) {
    const nextCurrency = cart.currency || window.VH?.currency || 'USD';
    if (currency !== nextCurrency) {
      currency = nextCurrency;
      formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {style:'currency', currency});
    }
    return formatter.format((Number(amount) || 0) / 100);
  }
  function safeUrl(value, fallback = '#') {
    try {
      const url = new URL(value, location.origin);
      return /^https?:$/.test(url.protocol) ? url.href : fallback;
    } catch { return fallback; }
  }
  function imageUrl(value) {
    const safe = safeUrl(value, '');
    if (!safe) return '';
    const url = new URL(safe);
    url.searchParams.set('width', '192');
    return url.href;
  }
  function setBusy(value, message = '') {
    busy = value;
    drawer.setAttribute('aria-busy', String(value));
    content.querySelectorAll('[data-lc-delta],[data-lc-quantity],[data-lc-remove]').forEach(node => { node.disabled = value || uncertain; });
    checkout.disabled = value || uncertain || !cart.item_count;
    document.querySelectorAll('[data-luciano-product-form] button[type="submit"]').forEach(button => {
      if (value && !button.disabled) {
        button.dataset.lcDisabled = 'true';
        button.disabled = true;
      } else if (!value && button.dataset.lcDisabled) {
        delete button.dataset.lcDisabled;
        button.disabled = false;
      }
    });
    status.textContent = message;
  }
  function clearError() {
    errorBox.hidden = true;
    errorBox.replaceChildren();
  }
  function showError(message) {
    errorBox.hidden = false;
    const text = document.createElement('p');
    text.textContent = message || labels.error;
    const retry = document.createElement('button');
    retry.type = 'button';
    retry.dataset.lcRetry = '';
    retry.textContent = labels.retry;
    errorBox.replaceChildren(text, retry);
  }
  function tick() {
    if (!cart.item_count || !deadline) return;
    const seconds = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
    if (!seconds) {
      timerLabel.textContent = labels.expired;
      clock.hidden = true;
      clearInterval(timerInterval);
      timerInterval = undefined;
      return;
    }
    timerLabel.textContent = labels.countdown;
    clock.hidden = false;
    const text = `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    if (clock.textContent !== text) clock.textContent = text;
  }
  function refreshTimer() {
    clearInterval(timerInterval);
    timerInterval = undefined;
    if (!drawer.open || document.hidden || !cart.item_count) return;
    tick();
    if (deadline > Date.now()) timerInterval = setInterval(tick, 1000);
  }
  function saveDeadline() {
    if (!cart.item_count) {
      deadline = 0;
      try { sessionStorage.removeItem(timerKey); } catch {}
      refreshTimer();
      return;
    }
    if (!deadline) {
      try { deadline = Number(sessionStorage.getItem(timerKey)) || 0; } catch {}
      if (!deadline || deadline > Date.now() + 600000) deadline = Date.now() + 600000;
      try { sessionStorage.setItem(timerKey, String(deadline)); } catch {}
    }
    refreshTimer();
  }
  function bundleId(item) {
    const value = item.properties?.__kaching_bundles;
    if (!value) return '';
    try { return JSON.parse(value).id || ''; }
    catch {
      try { return JSON.parse(atob(value)).id || ''; } catch { return ''; }
    }
  }
  function render(nextCart) {
    if (!Array.isArray(nextCart?.items) || typeof nextCart.total_price !== 'number') throw new Error(labels.error);
    cart = nextCart;
    lastSync = Date.now();
    uncertain = false;
    const count = cart.item_count || 0;
    select('[data-lc-count]').textContent = `(${count} ${count === 1 ? labels.item : labels.items})`;
    document.querySelectorAll('[data-cart-count]').forEach(node => { node.textContent = String(count); });
    timer.hidden = !count;
    select('[data-lc-shipping]').hidden = !count;
    select('[data-lc-footer]').hidden = !count;
    const savings = Math.max(0, Number(cart.original_total_price || 0) - cart.total_price);
    select('[data-lc-savings-row]').hidden = !savings;
    select('[data-lc-savings-value]').textContent = money(savings);
    select('[data-lc-total]').textContent = money(cart.total_price);
    if (!count) {
      content.innerHTML = `<div class="lc-empty"><p>${escape(labels.empty)}</p><button type="button" data-lc-close>${escape(labels.continueShopping)}</button></div>`;
    } else {
      content.innerHTML = cart.items.map(item => {
        const title = item.product_title || item.title;
        const image = imageUrl(item.featured_image?.url || item.image);
        const options = [];
        if (item.variant_title && item.variant_title !== 'Default Title') options.push(item.variant_title);
        const sellingPlan = item.selling_plan_allocation?.selling_plan?.name;
        if (sellingPlan) options.push(sellingPlan);
        for (const [key, value] of Object.entries(item.properties || {})) {
          if (!key.startsWith('_') && value != null && value !== '') options.push(`${key}: ${value}`);
        }
        const discounts = (item.line_level_discount_allocations || []).map(discount => discount.discount_application?.title).filter(Boolean);
        const final = item.final_line_price ?? item.line_price;
        const original = item.original_line_price ?? final;
        return `<article class="lc-item" data-lc-key="${escape(item.key)}">${image ? `<img class="lc-item-image" src="${escape(image)}" width="96" height="112" alt="${escape(title)}" loading="lazy" decoding="async">` : '<div class="lc-item-image" aria-hidden="true"></div>'}<div class="lc-item-info"><a class="lc-item-title" href="${escape(safeUrl(item.url))}">${escape(title)}</a>${options.map(option => `<p class="lc-item-options">${escape(option)}</p>`).join('')}<strong class="lc-item-price">${escape(money(final))}</strong>${original > final ? `<s class="lc-item-compare">${escape(money(original))}</s>` : ''}${discounts.map(discount => `<p class="lc-item-options">${escape(discount)}</p>`).join('')}<div>${item.properties?.__kaching_bundles ? `<p class="lc-item-options">${escape(labels.quantity)}: ${item.quantity}</p>` : `<div class="lc-quantity"><button type="button" data-lc-delta="-1" aria-label="${escape(labels.decrease + ': ' + title)}">−</button><input type="number" inputmode="numeric" min="0" step="1" value="${item.quantity}" data-lc-quantity aria-label="${escape(labels.quantity + ': ' + title)}"><button type="button" data-lc-delta="1" aria-label="${escape(labels.increase + ': ' + title)}">+</button></div>`}<button type="button" data-lc-remove>${escape(labels.remove)}</button></div></div></article>`;
      }).join('');
    }
    checkout.disabled = busy || !count;
    saveDeadline();
  }
  function open(source) {
    if (!drawer.open) {
      const started = performance.now();
      opener = source || document.activeElement;
      oldOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      drawer.showModal();
      drawer.dataset.lcOpenMs = (performance.now() - started).toFixed(1);
      requestAnimationFrame(() => { drawer.dataset.lcFrameMs = (performance.now() - started).toFixed(1); });
    }
    refreshTimer();
  }
  drawer.addEventListener('close', () => {
    document.documentElement.style.overflow = oldOverflow;
    refreshTimer();
    if (opener?.isConnected) opener.focus({preventScroll:true});
  });
  drawer.addEventListener('click', event => {
    if (event.target === drawer) {
      const rect = drawer.getBoundingClientRect();
      if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) drawer.close();
    }
  });
  function parseSection(data) {
    const html = data?.sections?.['luciano-cart-state'];
    if (!html) return null;
    const section = new DOMParser().parseFromString(html, 'text/html');
    const json = section.querySelector('[data-lc-cart-state]');
    if (!json) return null;
    try { return JSON.parse(json.textContent); } catch { return null; }
  }
  async function readCart() {
    if (reading) return reading;
    const version = generation;
    drawer.dataset.lcReads = String(Number(drawer.dataset.lcReads || 0) + 1);
    reading = window.fetch(`${root}cart.js`, {headers:{Accept:'application/json'}, cache:'no-store'})
      .then(async response => {
        if (!response.ok) throw new Error(labels.error);
        const nextCart = await response.json();
        if (version === generation) render(nextCart);
        return nextCart;
      }).finally(() => { reading = undefined; });
    return reading;
  }
  async function refreshCart() {
    if (busy) return;
    clearError();
    setBusy(true, labels.updating);
    try { await readCart(); }
    catch (error) { uncertain = true; showError(error.message); }
    finally { setBusy(false); }
  }
  async function add(event) {
    if (event.defaultPrevented) return;
    event.preventDefault();
    if (busy) return;
    const form = event.currentTarget;
    const submitter = event.submitter || form.querySelector('[data-add-button]');
    if (uncertain) { open(submitter); refreshCart(); return; }
    const body = new FormData(form);
    body.set('sections', 'luciano-cart-state');
    body.set('sections_url', location.pathname);
    clearError();
    generation++;
    reading = undefined;
    setBusy(true, labels.loading);
    if (!cart.item_count) content.innerHTML = '<div class="lc-loading" aria-hidden="true"><span></span><div><i></i><i></i><i></i></div></div>';
    open(submitter);
    const started = performance.now();
    try {
      const response = await window.fetch(`${root}cart/add.js`, {method:'POST', headers:{Accept:'application/json'}, body});
      const data = await response.json();
      if (!response.ok || data.status >= 400) {
        render(cart);
        throw new Error(typeof data.description === 'string' ? data.description : labels.error);
      }
      const nextCart = parseSection(data);
      if (nextCart) render(nextCart);
      else await readCart();
      drawer.dataset.lcConfirmMs = (performance.now() - started).toFixed(1);
      setBusy(false, labels.added);
    } catch (error) {
      uncertain = true;
      showError(error.message);
      setBusy(false);
    }
  }
  async function change(key, quantity) {
    if (busy || uncertain) return;
    const item = cart.items.find(line => line.key === key);
    if (!item) return;
    const nextQuantity = Math.max(0, Math.floor(Number(quantity) || 0));
    if (nextQuantity === item.quantity) return;
    const bundle = bundleId(item);
    if (item.properties?.__kaching_bundles && nextQuantity !== 0) return;
    const updates = bundle ? Object.fromEntries(cart.items.filter(line => bundleId(line) === bundle).map(line => [line.key, 0])) : null;
    const index = cart.items.indexOf(item);
    clearError();
    generation++;
    reading = undefined;
    setBusy(true, labels.updating);
    try {
      const response = await window.fetch(`${root}cart/${updates ? 'update' : 'change'}.js`, {
        method:'POST', headers:{'Content-Type':'application/json', Accept:'application/json'},
        body:JSON.stringify(updates ? {updates} : {id:key, quantity:nextQuantity})
      });
      const data = await response.json();
      if (!response.ok || data.status >= 400) throw new Error(typeof data.description === 'string' ? data.description : labels.error);
      render(data);
      setBusy(false);
      const nextLine = content.querySelectorAll('[data-lc-key]')[Math.min(index, cart.items.length - 1)];
      (nextLine?.querySelector('[data-lc-quantity]') || select('[data-lc-close]')).focus({preventScroll:true});
    } catch (error) {
      uncertain = true;
      showError(error.message);
      setBusy(false);
    }
  }
  function bind(scope = document) {
    scope.querySelectorAll('[data-luciano-product-form]').forEach(form => {
      if (form.dataset.lcBound) return;
      form.dataset.lcBound = 'true';
      form.addEventListener('submit', add);
    });
    scope.querySelectorAll('[data-cart-open]').forEach(link => {
      link.removeAttribute('data-cart-open');
      link.setAttribute('data-lc-open', '');
      link.setAttribute('aria-haspopup', 'dialog');
    });
  }
  document.addEventListener('click', event => {
    const openButton = event.target.closest('[data-lc-open]');
    if (openButton) {
      event.preventDefault();
      open(openButton);
      if (!busy && (uncertain || Date.now() - lastSync > 30000)) refreshCart();
      return;
    }
    if (!drawer.contains(event.target)) return;
    if (event.target.closest('[data-lc-close]')) { drawer.close(); return; }
    if (event.target.closest('[data-lc-retry]')) { refreshCart(); return; }
    const button = event.target.closest('[data-lc-delta],[data-lc-remove]');
    if (!button) return;
    const line = button.closest('[data-lc-key]');
    const item = cart.items.find(entry => entry.key === line.dataset.lcKey);
    if (item) change(item.key, button.hasAttribute('data-lc-remove') ? 0 : item.quantity + Number(button.dataset.lcDelta));
  });
  drawer.addEventListener('change', event => {
    if (!event.target.matches('[data-lc-quantity]')) return;
    change(event.target.closest('[data-lc-key]').dataset.lcKey, event.target.value);
  });
  checkout.form.addEventListener('submit', event => { if (busy || uncertain || !cart.item_count) event.preventDefault(); });
  document.addEventListener('visibilitychange', refreshTimer);
  window.addEventListener('pageshow', event => { if (event.persisted) { uncertain = true; lastSync = 0; if (drawer.open) refreshCart(); } });
  document.addEventListener('shopify:section:load', event => bind(event.target));
  render(cart);
  bind();
})();
